import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { toast } from "sonner";
import {
  Sparkles, Wand2, RefreshCw, ListChecks, MessageSquareQuote, Link2, ImageIcon, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import CampaignImageLibrary from "../engagement/CampaignImageLibrary";

/**
 * Maps the wide variety of axios error shapes into a single, actionable
 * admin-facing string.
 *
 *   - timeout / aborted          → "AI provider timed out (X seconds)…"
 *   - no response (network)      → "Network error reaching the AI service…"
 *   - 401/403                    → "Your admin session expired — sign in again."
 *   - 422 (Pydantic validation)  → "Invalid request: <field msg>"
 *   - 5xx                        → "Server error (status). Please try again."
 *   - other                      → fall back to backend `detail` or default.
 */
function explainAiError(err, opName = "request", elapsedMs = 0) {
  const code = err?.code;
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // Always log the full error to the console so admins can copy/paste
  // the diagnostic into a bug report. Sonner toasts are short by design;
  // DevTools is where the real signal lives.
  // eslint-disable-next-line no-console
  console.error(`[ai-copilot] ${opName} failed`, {
    code, status, elapsedMs, message: err?.message,
    url: err?.config?.url, method: err?.config?.method,
    detail, responseHeaders: err?.response?.headers,
  });

  if (code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    const secs = Math.round(elapsedMs / 1000);
    return `AI provider timed out${secs ? ` after ${secs}s` : ""}. Try a more focused topic, or click Retry.`;
  }
  if (!err?.response) {
    // Surface the axios `code` (e.g. ERR_NETWORK / ERR_BAD_REQUEST) so we
    // can tell whether this is CORS, DNS, or a connection reset.
    const reason = code || "no response";
    return `Network error reaching the AI service (${reason}). Open DevTools → Network tab and share the failing request URL if it persists.`;
  }
  if (status === 401 || status === 403) {
    return "Your admin session expired. Please refresh the page and sign in again.";
  }
  if (status === 422 && Array.isArray(detail)) {
    const msgs = detail.map((d) => (d?.msg ? `${(d?.loc || []).join(".")}: ${d.msg}` : "")).filter(Boolean);
    return msgs.length ? `Invalid request — ${msgs.join("; ")}` : "Invalid request payload.";
  }
  if (status === 504 || status === 502) {
    return `Server proxy timeout (${status}) on ${opName}. Try a shorter topic — non-English content sometimes needs a more focused prompt.`;
  }
  if (status >= 500) {
    return `Server error (${status}) on ${opName}. Please try again in a moment — if it persists, check backend logs.`;
  }
  const fallback = formatApiErrorDetail(detail);
  if (!detail) return `${opName} failed (HTTP ${status}). Please try again.`;
  return fallback;
}

/**
 * Maps the structured `fallback_reason` returned by the backend's
 * /draft endpoint to a friendly admin-facing toast.
 */
function explainFallbackReason(reason) {
  switch (reason) {
    case "no_key":          return "Generated with offline templates — EMERGENT_LLM_KEY not configured.";
    case "sdk_import":      return "Generated with offline templates — LLM SDK not installed on server.";
    case "timeout":         return "AI took too long — used a safe offline template. Click Generate again for a fresh AI draft.";
    case "malformed_json":  return "AI returned a malformed response — used a safe template. Click Generate again.";
    case "provider_error":  return "AI provider error — used a safe offline template. Click Generate again.";
    case "no_response":     return "AI gave no response — used a safe template. Click Generate again.";
    case "banned_word":     return "AI draft tripped our wellness-language guard — used a safer template.";
    default:                return "Draft created from offline templates (LLM unavailable).";
  }
}

/**
 * Article AI Copilot
 *
 * Sits at the top of the article editor dialog. The admin types a topic,
 * picks a condition + emotional angle + locale, hits "Generate full draft"
 * and the editor fields below are populated.
 *
 * Inline helpers (Rewrite hook / Improve SEO / Generate FAQ / Generate CTA /
 * Suggest links / Generate hero or secondary image) call the same API but
 * scoped to specific fields, so the admin can iterate without re-drafting
 * the whole article.
 *
 * Props:
 *   value         — current article state (the same `editing` object
 *                   used by ArticlesTab)
 *   onChange(p)   — merge a partial update into `editing`
 *   onApplyFaq(faq) — admin clicked "Append FAQ to body" — receives the
 *                     {q,a}[] list
 *   onApplyCta({text,url}) — admin chose a CTA — receives the chosen pair
 */
export default function ArticleAiCopilot({ value, onChange, onApplyFaq, onApplyCta }) {
  const [meta, setMeta] = useState(null);
  const [topic, setTopic] = useState("");
  const [condition, setCondition] = useState("");
  const [angle, setAngle] = useState("curious_why");
  const [locale, setLocale] = useState("en");
  const [targetKw, setTargetKw] = useState("");
  const [busyKey, setBusyKey] = useState(""); // which AI op is running
  const [open, setOpen] = useState(true);

  // Result panels
  const [hookOpts, setHookOpts] = useState(null); // {titles:[], hero_excerpt}
  const [seoOpts, setSeoOpts] = useState(null);   // {meta_description, slug, h2_suggestions}
  const [faqOpts, setFaqOpts] = useState(null);   // {faq:[{q,a}]}
  const [ctaOpts, setCtaOpts] = useState(null);   // {ctas:[{text,url}]}
  const [linkOpts, setLinkOpts] = useState(null); // {links:[{slug,title,url,reason}]}
  const [libRole, setLibRole] = useState("");     // 'hero' | 'secondary' — when picking from library

  useEffect(() => {
    api.get("/admin/learn/ai/meta").then((r) => setMeta(r.data)).catch(() => setMeta(null));
  }, []);

  // When the parent passes an existing article (admin clicked Edit), pre-fill
  // the copilot inputs from its existing values so AI calls are contextual.
  useEffect(() => {
    if (value?.title && !topic) setTopic(value.title);
    if (value?.condition_key && !condition) setCondition(value.condition_key);
  }, [value?.title, value?.condition_key]); // eslint-disable-line react-hooks/exhaustive-deps

  const isBusy = (k) => busyKey === k;
  const [draftElapsed, setDraftElapsed] = useState(0);
  const draftElapsedTimer = useRef(null);

  // Tick a "Generating… 12s" counter while the draft call is in flight.
  // Resets on unmount + when the call completes/fails.
  useEffect(() => {
    if (busyKey === "draft") {
      const startedAt = Date.now();
      setDraftElapsed(0);
      draftElapsedTimer.current = setInterval(() => {
        setDraftElapsed(Math.round((Date.now() - startedAt) / 1000));
      }, 1000);
    } else {
      if (draftElapsedTimer.current) clearInterval(draftElapsedTimer.current);
      draftElapsedTimer.current = null;
      setDraftElapsed(0);
    }
    return () => {
      if (draftElapsedTimer.current) clearInterval(draftElapsedTimer.current);
    };
  }, [busyKey]);

  // ---- Multilingual translation ----------------------------------------
  // Phase-1 locales have a primary button; Phase-2 stubs are hidden behind
  // a "More languages soon" disclosure so the architecture is ready when
  // we activate them server-side.
  const PHASE1_LOCALES = useMemo(() => ([
    { code: "hi", label: "Hindi", flag: "🇮🇳", est: "~8s" },
    { code: "fr", label: "French", flag: "🇫🇷", est: "~5s" },
    { code: "ar", label: "Arabic", flag: "🇸🇦", est: "~5s" },
  ]), []);
  const PHASE2_LOCALES = useMemo(() => ([
    { code: "de", label: "German" }, { code: "it", label: "Italian" },
    { code: "nl", label: "Dutch" }, { code: "ja", label: "Japanese" },
    { code: "ms", label: "Malay" }, { code: "id", label: "Indonesian" },
    { code: "zh-Hant", label: "Traditional Chinese" },
    { code: "pa", label: "Punjabi" }, { code: "gu", label: "Gujarati" },
  ]), []);
  const [translations, setTranslations] = useState({});   // { locale: { ...fields, generatedAt } }
  const [translateLocale, setTranslateLocale] = useState(""); // which locale is being generated
  const [previewLocale, setPreviewLocale] = useState(""); // which translation is being previewed
  const [showMoreLocales, setShowMoreLocales] = useState(false);

  const runTranslate = async (targetLocale, { overwrite = false } = {}) => {
    if (!value?.body_md?.trim() && !value?.title?.trim()) {
      return toast.error("Generate or paste an English draft first.");
    }
    if (translations[targetLocale] && !overwrite) {
      const ok = window.confirm(
        `You already have a ${targetLocale.toUpperCase()} translation in this session. Overwrite it?`
      );
      if (!ok) return;
    }
    setTranslateLocale(targetLocale);
    const startedAt = Date.now();
    const startedToast = toast.loading(`Translating to ${targetLocale.toUpperCase()}…`, { duration: Infinity });
    try {
      const { data } = await api.post(
        "/admin/learn/ai/translate",
        {
          locale: targetLocale,
          title: value.title || "",
          slug: value.slug || "",
          meta_description: value.meta_description || "",
          hero_excerpt: value.hero_excerpt || "",
          body_md: value.body_md || "",
          faq: faqOpts?.faq || value._faq || [],
          cta: ctaOpts?.ctas?.[0] || value._cta || { text: "", url: "" },
          persist: !!value?.id,
          entity_id: value?.id || undefined,
          overwrite_existing: overwrite,
        },
        { timeout: 90_000 },
      );
      toast.dismiss(startedToast);
      const secs = Math.round((Date.now() - startedAt) / 1000);

      if (data.needs_overwrite_confirm) {
        const ok = window.confirm(
          `A ${targetLocale.toUpperCase()} translation already exists in the database for this article. Overwrite it?`
        );
        if (!ok) {
          toast.message("Kept existing translation.");
          return;
        }
        // Re-run with overwrite=true
        return runTranslate(targetLocale, { overwrite: true });
      }

      const t = data.translated || {};
      setTranslations((prev) => ({ ...prev, [targetLocale]: { ...t, generatedAt: Date.now(), persisted: data.persisted } }));
      setPreviewLocale(targetLocale);
      if (data.used_fallback) {
        toast.warning(explainFallbackReason(data.fallback_reason), { duration: 8000 });
      } else if (data.persisted) {
        toast.success(`${targetLocale.toUpperCase()} translation saved in ${secs}s.`);
      } else {
        toast.success(`${targetLocale.toUpperCase()} translation ready (${secs}s). Save the English article to persist.`);
      }
    } catch (e) {
      toast.dismiss(startedToast);
      toast.error(explainAiError(e, `${targetLocale.toUpperCase()} translation`, Date.now() - startedAt), { duration: 10000 });
    } finally {
      setTranslateLocale("");
    }
  };

  const runDraft = async () => {
    if (!topic.trim()) return toast.error("Add a topic first");
    setBusyKey("draft");
    const startedAt = Date.now();
    // Initial loading toast — replaced by status-specific copy as we poll.
    let toastId = toast.loading("Preparing AI draft…", { duration: Infinity });
    try {
      // 1) Submit — returns in <100ms with { job_id, status: "queued" }
      const { data: enq } = await api.post(
        "/admin/learn/ai/draft",
        {
          topic: topic.trim(),
          condition_key: condition || null,
          emotional_angle: angle,
          target_keyword: targetKw || null,
          locale,
        },
        { timeout: 10_000 },
      );
      const jobId = enq?.job_id;
      if (!jobId) throw new Error("Backend did not return a job_id");

      // 2) Poll every 2.5s. Backend keeps the job row for 30 min.
      let lastStatus = "queued";
      let polls = 0;
      const MAX_POLLS = 60;        // 60 × 2.5s = 150s ceiling
      while (polls < MAX_POLLS) {
        await new Promise((r) => setTimeout(r, 2500));
        polls += 1;
        const { data } = await api.get(`/admin/learn/ai/job/${jobId}`, { timeout: 10_000 });

        if (data.status !== lastStatus) {
          lastStatus = data.status;
          toast.dismiss(toastId);
          if (data.status === "processing") {
            toastId = toast.loading("AI is writing your article…", { duration: Infinity });
          } else if (data.status === "retrying") {
            const last = data.providers_attempted?.slice(-1)?.[0];
            toastId = toast.loading(
              `Primary AI took too long — switching engines${last?.provider ? ` (${last.provider} → next)` : ""}…`,
              { duration: Infinity },
            );
          }
        }

        if (data.status === "completed" || data.status === "fallback_completed") {
          toast.dismiss(toastId);
          const a = data.result || {};
          onChange({
            title: a.title || value.title,
            slug: a.slug || value.slug,
            condition_key: a.condition_key || condition || value.condition_key,
            meta_description: a.meta_description || value.meta_description,
            hero_excerpt: a.hero_excerpt || value.hero_excerpt,
            body_md: a.body_md || value.body_md,
            related_keys: a.related_keys || value.related_keys,
            tags: a.tags?.length ? a.tags : value.tags,
            estimated_read_minutes: a.estimated_read_minutes || value.estimated_read_minutes,
            _ai_hero_theme: a.hero_image_theme,
            _ai_secondary_theme: a.secondary_image_theme,
          });
          if (a.faq?.length) setFaqOpts({ faq: a.faq });
          if (a.cta?.text) setCtaOpts({ ctas: [a.cta] });
          const secs = Math.round((Date.now() - startedAt) / 1000);
          if (data.status === "fallback_completed") {
            toast.warning(
              `${explainFallbackReason(data.fallback_reason)} (attempts: ${(data.retry_count || 0) + 1})`,
              { duration: 9000 },
            );
          } else {
            const prov = data.provider_used ? ` via ${data.provider_used}` : "";
            toast.success(`Draft ready in ${secs}s${prov}. Review and edit below.`);
          }
          return;
        }

        if (data.status === "failed") {
          toast.dismiss(toastId);
          toast.error(
            `Generation failed: ${data.error || data.fallback_reason || "unknown"}.`,
            { duration: 12000, action: { label: "Retry", onClick: () => runDraft() } },
          );
          return;
        }
      }

      toast.dismiss(toastId);
      toast.error("AI is taking unusually long. The job is still running in the background — refresh in a minute and try again.", {
        duration: 12000,
        action: { label: "Retry", onClick: () => runDraft() },
      });
    } catch (e) {
      toast.dismiss(toastId);
      const msg = explainAiError(e, "Article draft", Date.now() - startedAt);
      toast.error(msg, {
        duration: 12000,
        action: { label: "Retry", onClick: () => runDraft() },
      });
    } finally {
      setBusyKey("");
    }
  };


  const runRewriteHook = async () => {
    if (!value?.title?.trim()) return toast.error("Add a title first");
    setBusyKey("hook");
    try {
      const { data } = await api.post("/admin/learn/ai/rewrite-hook", {
        title: value.title, hero_excerpt: value.hero_excerpt || "",
        condition_key: value.condition_key || null, locale,
      });
      setHookOpts(data);
    } catch (e) {
      toast.error(explainAiError(e, "Hook rewrite", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const runImproveSeo = async () => {
    if (!value?.title?.trim()) return toast.error("Add a title first");
    setBusyKey("seo");
    try {
      const { data } = await api.post("/admin/learn/ai/improve-seo", {
        title: value.title, body_md: value.body_md || "",
        target_keyword: targetKw || null, locale,
      });
      setSeoOpts(data);
    } catch (e) {
      toast.error(explainAiError(e, "SEO improvement", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const runFaq = async () => {
    if (!value?.body_md || value.body_md.length < 30) return toast.error("Write a body first");
    setBusyKey("faq");
    try {
      const { data } = await api.post("/admin/learn/ai/faq", {
        title: value.title || topic, body_md: value.body_md, locale,
      });
      setFaqOpts(data);
    } catch (e) {
      toast.error(explainAiError(e, "FAQ generation", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const runCta = async () => {
    if (!value?.title?.trim()) return toast.error("Add a title first");
    setBusyKey("cta");
    try {
      const { data } = await api.post("/admin/learn/ai/cta", {
        title: value.title, body_md: value.body_md || "", locale,
      });
      setCtaOpts(data);
    } catch (e) {
      toast.error(explainAiError(e, "CTA suggestions", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const runLinks = async () => {
    if (!value?.title?.trim()) return toast.error("Add a title first");
    setBusyKey("links");
    try {
      const { data } = await api.post("/admin/learn/ai/internal-links", {
        title: value.title, body_md: value.body_md || "",
        exclude_slug: value.slug || null,
      });
      setLinkOpts(data);
    } catch (e) {
      toast.error(explainAiError(e, "Link suggestions", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const generateImage = async (role) => {
    const theme = role === "hero"
      ? (value?._ai_hero_theme || `${value?.title || topic} — hero image`)
      : (value?._ai_secondary_theme || `${value?.title || topic} — secondary lifestyle image`);
    if (!theme.trim()) return toast.error("Add a topic or title first");
    setBusyKey(`image-${role}`);
    try {
      const { data } = await api.post("/admin/learn/ai/image", {
        topic: value?.title || topic,
        role,
        emotional_angle: angle,
      });
      if (data?.url) {
        if (role === "hero") onChange({ hero_image_url: data.url });
        else onChange({ secondary_image_url: data.url });
        toast.success(`AI ${role} image generated + saved to Library`);
      }
    } catch (e) {
      toast.error(explainAiError(e, "Image generation", 0), { duration: 8000 });
    } finally { setBusyKey(""); }
  };

  const conditions = meta?.conditions || [];
  const angles = meta?.emotional_angles || [];
  const locales = meta?.locales || ["en"];

  return (
    <div
      className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 p-4 mb-4"
      data-testid="article-ai-copilot"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 select-none"
        data-testid="article-ai-toggle"
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-emerald-700 text-white">
            <Wand2 className="w-3.5 h-3.5" />
          </span>
          <div className="text-left">
            <div className="text-sm font-semibold text-emerald-950">Wellness Article Copilot</div>
            <div className="text-[11px] text-emerald-900/65">
              AI-assisted, curiosity-driven, SEO-ready · multilingual
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-emerald-700" /> : <ChevronDown className="w-4 h-4 text-emerald-700" />}
      </button>

      {!open ? null : (
        <div className="mt-4 space-y-3">
          {/* Inputs */}
          <div className="grid sm:grid-cols-2 gap-2">
            <div className="sm:col-span-2">
              <Label className="text-[11px] text-emerald-900/70">Topic — describe the real-life struggle</Label>
              <Textarea
                rows={2}
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder='e.g. "Why do some people still feel tired after sleeping?"'
                className="mt-1 rounded-lg"
                data-testid="article-ai-topic"
              />
            </div>
            <div>
              <Label className="text-[11px] text-emerald-900/70">Condition</Label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value)}
                className="mt-1 w-full rounded-lg border border-emerald-200 bg-white text-sm px-2.5 py-2"
                data-testid="article-ai-condition"
              >
                <option value="">(open / general)</option>
                {conditions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-emerald-900/70">Emotional angle</Label>
              <select
                value={angle}
                onChange={(e) => setAngle(e.target.value)}
                className="mt-1 w-full rounded-lg border border-emerald-200 bg-white text-sm px-2.5 py-2"
                data-testid="article-ai-angle"
              >
                {angles.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-emerald-900/70">Language</Label>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value)}
                className="mt-1 w-full rounded-lg border border-emerald-200 bg-white text-sm px-2.5 py-2"
                data-testid="article-ai-locale"
              >
                {locales.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[11px] text-emerald-900/70">Target keyword (optional)</Label>
              <Input
                value={targetKw}
                onChange={(e) => setTargetKw(e.target.value)}
                placeholder="e.g. afternoon energy dip"
                className="mt-1 rounded-lg"
                data-testid="article-ai-keyword"
              />
            </div>
          </div>

          {/* Main action */}
          <Button
            onClick={runDraft}
            disabled={isBusy("draft")}
            className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
            data-testid="article-ai-draft-btn"
          >
            {isBusy("draft") ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {isBusy("draft")
              ? (draftElapsed >= 1 ? `Generating… ${draftElapsed}s` : "Generating…")
              : "Generate full article draft"}
          </Button>
          {isBusy("draft") && (
            <p className="text-[11px] text-emerald-900/65 -mt-1.5 text-center" data-testid="article-ai-draft-hint">
              Calm and steady — Claude usually returns in 25-60s. You can keep editing other fields meanwhile.
            </p>
          )}

          {/* Inline helpers */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
            <SmallAction onClick={runRewriteHook} busy={isBusy("hook")} icon={RefreshCw} label="Rewrite hook" testid="article-ai-hook-btn" />
            <SmallAction onClick={runImproveSeo} busy={isBusy("seo")} icon={ListChecks} label="Improve SEO" testid="article-ai-seo-btn" />
            <SmallAction onClick={runFaq} busy={isBusy("faq")} icon={MessageSquareQuote} label="Generate FAQ" testid="article-ai-faq-btn" />
            <SmallAction onClick={runCta} busy={isBusy("cta")} icon={Sparkles} label="Suggest CTAs" testid="article-ai-cta-btn" />
            <SmallAction onClick={runLinks} busy={isBusy("links")} icon={Link2} label="Internal links" testid="article-ai-links-btn" />
            <SmallAction onClick={() => generateImage("hero")} busy={isBusy("image-hero")} icon={ImageIcon} label="Hero image" testid="article-ai-image-hero-btn" />
            <SmallAction onClick={() => generateImage("secondary")} busy={isBusy("image-secondary")} icon={ImageIcon} label="Secondary image" testid="article-ai-image-secondary-btn" />
            <SmallAction onClick={() => setLibRole("hero")} busy={false} icon={ImageIcon} label="Pick hero from library" testid="article-ai-lib-hero-btn" />
            <SmallAction onClick={() => setLibRole("secondary")} busy={false} icon={ImageIcon} label="Pick 2nd from library" testid="article-ai-lib-secondary-btn" />
          </div>

          {/* ---- Translate this draft ---- */}
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/30 p-3 sm:p-4 space-y-2.5" data-testid="article-ai-translate-block">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              <Sparkles className="w-3 h-3" /> Translate this draft
            </div>
            <p className="text-[12px] text-emerald-900/70 leading-relaxed">
              One-click localisation using the same resilient pipeline. Markdown,
              FAQ, CTA, and inline link URLs are preserved exactly.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {PHASE1_LOCALES.map((loc) => {
                const has = !!translations[loc.code];
                return (
                  <Button
                    key={loc.code}
                    size="sm"
                    variant="outline"
                    onClick={() => runTranslate(loc.code)}
                    disabled={!!translateLocale}
                    className="rounded-full text-xs border-emerald-200 text-emerald-900 hover:bg-emerald-50"
                    data-testid={`article-ai-translate-${loc.code}-btn`}
                  >
                    {translateLocale === loc.code ? (
                      <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                    ) : (
                      <span className="mr-1.5" aria-hidden="true">{loc.flag}</span>
                    )}
                    {translateLocale === loc.code ? `Translating to ${loc.label}…` : `Translate to ${loc.label}`}
                    {has && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-emerald-600" aria-label="translated" />}
                    <span className="ml-1.5 text-[10px] text-emerald-700/65">{loc.est}</span>
                  </Button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowMoreLocales((v) => !v)}
                className="text-[11px] text-emerald-700 hover:text-emerald-800 underline ml-1"
                data-testid="article-ai-more-locales-toggle"
              >
                {showMoreLocales ? "Hide" : "More languages soon"}
              </button>
            </div>
            {showMoreLocales && (
              <div className="flex flex-wrap gap-1.5 pt-1" data-testid="article-ai-phase2-locales">
                {PHASE2_LOCALES.map((loc) => (
                  <Button
                    key={loc.code}
                    size="sm"
                    variant="outline"
                    onClick={() => runTranslate(loc.code)}
                    disabled={!!translateLocale}
                    className="rounded-full text-[11px] border-emerald-100 text-emerald-900/65 hover:bg-emerald-50"
                    data-testid={`article-ai-translate-${loc.code}-btn`}
                  >
                    {translateLocale === loc.code ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : null}
                    {loc.label}
                  </Button>
                ))}
              </div>
            )}

            {/* Locale tabs preview — appears once at least one translation exists */}
            {Object.keys(translations).length > 0 && (
              <div className="pt-2.5 border-t border-emerald-100" data-testid="article-ai-translate-preview">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <button
                    type="button"
                    onClick={() => setPreviewLocale("")}
                    className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-semibold border ${previewLocale === "" ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"}`}
                    data-testid="article-ai-translate-tab-en"
                  >
                    EN (source)
                  </button>
                  {Object.keys(translations).map((code) => (
                    <button
                      key={code}
                      type="button"
                      onClick={() => setPreviewLocale(code)}
                      className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider font-semibold border ${previewLocale === code ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-emerald-900 border-emerald-200 hover:bg-emerald-50"}`}
                      data-testid={`article-ai-translate-tab-${code}`}
                    >
                      {code} {translations[code].persisted ? "·" : "•"}
                    </button>
                  ))}
                </div>
                {previewLocale && translations[previewLocale] && (
                  <div className="rounded-xl bg-white border border-emerald-100 p-3 space-y-2 text-sm" data-testid={`article-ai-translate-preview-${previewLocale}`}>
                    <div className="text-[11px] uppercase tracking-wider text-emerald-700/80 font-semibold flex items-center justify-between">
                      <span>{previewLocale.toUpperCase()} preview</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${translations[previewLocale].persisted ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {translations[previewLocale].persisted ? "saved" : "preview only"}
                      </span>
                    </div>
                    <p className="font-display text-base text-emerald-950 leading-snug">{translations[previewLocale].title}</p>
                    <p className="text-emerald-900/75 italic">{translations[previewLocale].meta_description}</p>
                    <p className="text-emerald-900/85 whitespace-pre-wrap line-clamp-6">{translations[previewLocale].body_md}</p>
                    {translations[previewLocale].cta?.text && (
                      <div className="text-[12px] text-emerald-800">
                        CTA: <span className="font-semibold">{translations[previewLocale].cta.text}</span>
                        {translations[previewLocale].cta.url && <span className="text-emerald-700/60"> → {translations[previewLocale].cta.url}</span>}
                      </div>
                    )}
                    {!translations[previewLocale].persisted && !value?.id && (
                      <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-2">
                        Save the English article first to persist this translation. Until then it lives in this session only.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>


          {/* Image preview row */}
          {(value?.hero_image_url || value?.secondary_image_url) && (
            <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid="article-ai-image-preview-row">
              {value.hero_image_url && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Hero</div>
                  <img src={value.hero_image_url} alt="Hero" loading="lazy" className="rounded-xl border border-emerald-100 aspect-[16/9] object-cover w-full" />
                </div>
              )}
              {value.secondary_image_url && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Secondary</div>
                  <img src={value.secondary_image_url} alt="Secondary" loading="lazy" className="rounded-xl border border-emerald-100 aspect-[16/9] object-cover w-full" />
                </div>
              )}
            </div>
          )}

          {/* Hook options */}
          {hookOpts && (
            <div className="rounded-xl border border-emerald-100 bg-white p-3" data-testid="article-ai-hook-options">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Title options</div>
              <ul className="space-y-1.5">
                {(hookOpts.titles || []).map((t, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="text-sm text-emerald-950 flex-1">{t}</span>
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { onChange({ title: t }); toast.success("Applied"); }} data-testid={`article-ai-hook-apply-${i}`}>Use</Button>
                  </li>
                ))}
              </ul>
              {hookOpts.hero_excerpt && (
                <div className="mt-2 pt-2 border-t border-emerald-50">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700/80 font-semibold mb-1">Suggested hero excerpt</div>
                  <p className="text-xs text-emerald-900/80 leading-relaxed">{hookOpts.hero_excerpt}</p>
                  <Button size="sm" className="mt-2 rounded-full" variant="outline" onClick={() => { onChange({ hero_excerpt: hookOpts.hero_excerpt }); toast.success("Applied"); }}>Use excerpt</Button>
                </div>
              )}
            </div>
          )}

          {/* SEO options */}
          {seoOpts && (
            <div className="rounded-xl border border-emerald-100 bg-white p-3" data-testid="article-ai-seo-options">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">SEO suggestions</div>
              {seoOpts.meta_description && (
                <div className="text-xs text-emerald-900/80 mb-2">
                  <span className="font-semibold">Meta:</span> {seoOpts.meta_description}
                  <Button size="sm" className="ml-2 rounded-full" variant="ghost" onClick={() => { onChange({ meta_description: seoOpts.meta_description }); toast.success("Applied"); }}>Use</Button>
                </div>
              )}
              {seoOpts.slug && (
                <div className="text-xs text-emerald-900/80 mb-2">
                  <span className="font-semibold">Slug:</span> /learn/{seoOpts.slug}
                  <Button size="sm" className="ml-2 rounded-full" variant="ghost" onClick={() => { onChange({ slug: seoOpts.slug }); toast.success("Applied"); }}>Use</Button>
                </div>
              )}
              {seoOpts.h2_suggestions?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700/80 font-semibold mb-1">H2 ideas</div>
                  <ul className="list-disc pl-4 text-xs text-emerald-900/80 space-y-0.5">
                    {seoOpts.h2_suggestions.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* FAQ options */}
          {faqOpts && (
            <div className="rounded-xl border border-emerald-100 bg-white p-3" data-testid="article-ai-faq-options">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Suggested FAQ</div>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => { onApplyFaq?.(faqOpts.faq || []); toast.success("Appended to body"); }} data-testid="article-ai-faq-apply-all">
                  Append all to body
                </Button>
              </div>
              <ul className="space-y-1.5 text-xs text-emerald-900/85">
                {(faqOpts.faq || []).map((f, i) => (
                  <li key={i}><span className="font-semibold">Q: </span>{f.q}<br /><span className="font-semibold">A: </span>{f.a}</li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA options */}
          {ctaOpts && (
            <div className="rounded-xl border border-emerald-100 bg-white p-3" data-testid="article-ai-cta-options">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">CTA ideas</div>
              <ul className="space-y-1">
                {(ctaOpts.ctas || []).map((c, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs text-emerald-900/85">
                    <span className="flex-1">{c.text} <span className="text-emerald-700/70">→ {c.url}</span></span>
                    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => { onApplyCta?.(c); toast.success("CTA set"); }} data-testid={`article-ai-cta-apply-${i}`}>Use</Button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Internal link options */}
          {linkOpts && (
            <div className="rounded-xl border border-emerald-100 bg-white p-3" data-testid="article-ai-links-options">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-2">Suggested internal links</div>
              <ul className="space-y-1.5">
                {(linkOpts.links || []).map((l, i) => (
                  <li key={i} className="text-xs text-emerald-900/85">
                    <a href={l.url} target="_blank" rel="noreferrer noopener" className="font-semibold text-emerald-700 hover:underline">
                      {l.title}
                    </a>
                    <span className="text-emerald-900/55"> — {l.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Library picker — chooses an existing image as hero or secondary */}
      <CampaignImageLibrary
        open={!!libRole}
        onClose={() => setLibRole("")}
        onSelect={(url) => {
          if (libRole === "hero") onChange({ hero_image_url: url });
          else if (libRole === "secondary") onChange({ secondary_image_url: url });
          setLibRole("");
        }}
        testid={`article-ai-${libRole || "lib"}-library`}
      />
    </div>
  );
}


function SmallAction({ onClick, busy, icon: Icon, label, testid }) {
  return (
    <Button
      type="button"
      onClick={onClick}
      disabled={busy}
      variant="outline"
      size="sm"
      className="rounded-full justify-start bg-white border-emerald-200 hover:bg-emerald-50 text-emerald-900"
      data-testid={testid}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Icon className="w-3.5 h-3.5 mr-1.5 text-emerald-700" />}
      <span className="text-xs">{label}</span>
    </Button>
  );
}
